import { ReportColumn } from '../models/report-definition';
import {
   ReportColumnSortConfig,
   ReportSortDirection,
   ReportSortState
} from '../models/report-sorting';
import { ReportRowVm } from '../models/report-row-vm';
import { REPORT_SORT_ORDERS } from './report-sorting.orders';

const collator = new Intl.Collator(undefined, {
   numeric: true,
   sensitivity: 'base'
});

function toText(value: unknown): string {
   return value === null || value === undefined ? '' : String(value).trim();
}

function toNumber(value: unknown): number | null {
   if (value === null || value === undefined || value === '') return null;

   if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
   }

   const raw = toText(value);
   if (!raw) return null;

   const isAccountingNegative = /^\(.+\)$/.test(raw);
   const cleaned = raw.replace(/[,$%\s()]/g, '');
   if (!cleaned) return null;

   const parsed = Number(cleaned);
   if (!Number.isFinite(parsed)) return null;

   return isAccountingNegative ? parsed * -1 : parsed;
}

function toDateMs(value: unknown): number | null {
   if (value === null || value === undefined || value === '') return null;

   if (value instanceof Date) {
      const time = value.getTime();
      return Number.isNaN(time) ? null : time;
   }

   const raw = toText(value);
   if (!raw) return null;

   const isoMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
   if (isoMatch) {
      return new Date(
         Number(isoMatch[1]),
         Number(isoMatch[2]) - 1,
         Number(isoMatch[3])
      ).getTime();
   }

   const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})/);
   if (slashMatch) {
      const yearText = slashMatch[3];
      const year = yearText.length === 2 ? Number(`20${yearText}`) : Number(yearText);

      return new Date(
         year,
         Number(slashMatch[2]) - 1,
         Number(slashMatch[1])
      ).getTime();
   }

   const parsed = new Date(raw);
   const time = parsed.getTime();

   return Number.isNaN(time) ? null : time;
}

function isNullSortValue(
   value: unknown,
   sortConfig: ReportColumnSortConfig
): boolean {
   if (value === null || value === undefined) return true;

   if (typeof value === 'string' && value.trim() === '') return true;

   if (sortConfig.type === 'number') return toNumber(value) === null;
   if (sortConfig.type === 'date') return toDateMs(value) === null;

   return false;
}

function compareNulls(
   aIsNull: boolean,
   bIsNull: boolean,
   sortConfig: ReportColumnSortConfig
): number | null {
   if (!aIsNull && !bIsNull) return null;
   if (aIsNull && bIsNull) return 0;

   const nulls = sortConfig.nulls ?? 'last';

   if (aIsNull) return nulls === 'first' ? -1 : 1;
   return nulls === 'first' ? 1 : -1;
}

function getColumnSortConfig(column: ReportColumn): ReportColumnSortConfig | null {
   if (column.sort === false || !column.sort) return null;

   return column.sort;
}

function getRawValue(
   row: ReportRowVm,
   column: ReportColumn,
   sortConfig: ReportColumnSortConfig
): unknown {
   return sortConfig.getValue ? sortConfig.getValue(row) : row.cells[column.key];
}

function normaliseCustomOrderValue(value: unknown): string {
   return toText(value).toLowerCase();
}

function compareCustomValues(
   aRaw: unknown,
   bRaw: unknown,
   sortConfig: ReportColumnSortConfig
): number {
   const order =
      sortConfig.customOrder ?? REPORT_SORT_ORDERS[sortConfig.customOrderKey ?? ''];

   if (!order?.length) {
      return collator.compare(toText(aRaw), toText(bRaw));
   }

   const orderMap = new Map(
      order.map((value, index) => [normaliseCustomOrderValue(value), index])
   );

   const aText = normaliseCustomOrderValue(aRaw);
   const bText = normaliseCustomOrderValue(bRaw);
   const aIndex = orderMap.get(aText);
   const bIndex = orderMap.get(bText);

   if (aIndex !== undefined && bIndex !== undefined) {
      return aIndex - bIndex;
   }

   if (aIndex !== undefined) return -1;
   if (bIndex !== undefined) return 1;

   return collator.compare(aText, bText);
}

function compareNonNullValues(
   aRaw: unknown,
   bRaw: unknown,
   sortConfig: ReportColumnSortConfig
): number {
   switch (sortConfig.type) {
      case 'number':
         return (toNumber(aRaw) ?? 0) - (toNumber(bRaw) ?? 0);

      case 'date':
         return (toDateMs(aRaw) ?? 0) - (toDateMs(bRaw) ?? 0);

      case 'custom':
         return compareCustomValues(aRaw, bRaw, sortConfig);

      case 'string':
         return collator.compare(toText(aRaw), toText(bRaw));
   }
}

function directionMultiplier(direction: ReportSortDirection): number {
   return direction === 'desc' ? -1 : 1;
}

export function applyReportSorting(
   rows: ReportRowVm[],
   columns: ReportColumn[],
   sortState: ReportSortState | null | undefined
): ReportRowVm[] {
   if (!sortState) return rows;

   const column = columns.find((item) => item.key === sortState.columnKey);
   if (!column) return rows;

   const sortConfig = getColumnSortConfig(column);
   if (!sortConfig) return rows;

   const multiplier = directionMultiplier(sortState.direction);

   return rows
      .map((row, originalIndex) => ({ row, originalIndex }))
      .sort((a, b) => {
         const aRaw = getRawValue(a.row, column, sortConfig);
         const bRaw = getRawValue(b.row, column, sortConfig);

         const nullComparison = compareNulls(
            isNullSortValue(aRaw, sortConfig),
            isNullSortValue(bRaw, sortConfig),
            sortConfig
         );

         if (nullComparison !== null && nullComparison !== 0) {
            return nullComparison;
         }

         const result = compareNonNullValues(aRaw, bRaw, sortConfig);

         if (result !== 0) return result * multiplier;

         return a.originalIndex - b.originalIndex;
      })
      .map((item) => item.row);
}
